/**
 * Weekly Agenda Header Component
 *
 * Navigation header for the weekly agenda view.
 * Shows week range label, menu/prev/next/today/add buttons.
 *
 * Follows single responsibility: weekly header UI only.
 */

import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { Colors, Shadows } from '@/constants/theme';
import { BorderRadius } from '@/constants/spacing';
import { getHeadingFont } from '@/constants/typography';
import { ScreenHeaderContainer } from '@/components/ui/ScreenHeaderContainer';
import { AddEventDropdown } from './AddEventDropdown';

type WeeklyAgendaHeaderProps = {
  headerLabel: string;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onTodayPress: () => void;
  onMenuPress: () => void;
  onAddEventPress?: () => void;
  onHeaderTextPress?: () => void;
  showCalendarSelector: boolean;
  /**
   * When true, render with a flat bottom (no rounded corners / no shadow)
   * so a sibling element below (e.g. the family avatars row) can carry the
   * rounded bottom of the visually-unified header zone. Safe-area-top +
   * cardBackground are still applied here so the header still sits cleanly
   * under the status bar.
   */
  bareContainer?: boolean;
};

export function WeeklyAgendaHeader({
  headerLabel,
  onPrevWeek,
  onNextWeek,
  onTodayPress,
  onMenuPress,
  onAddEventPress,
  onHeaderTextPress,
  showCalendarSelector,
  bareContainer = false,
}: WeeklyAgendaHeaderProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const insets = useSafeAreaInsets();

  const innerContent = (
    <>
      {/* Left: Menu Button */}
      <View style={styles.leftButtons}>
        <TouchableOpacity
          style={styles.iconButton}
          onPress={onMenuPress}
          activeOpacity={0.7}
        >
          <Ionicons
            name={showCalendarSelector ? 'close' : 'menu'}
            size={24}
            color={palette.tint}
          />
        </TouchableOpacity>
      </View>

      {/* Center: Week Navigation */}
      <View style={styles.navigationGroup}>
        <TouchableOpacity
          style={styles.iconButton}
          onPress={onPrevWeek}
          activeOpacity={0.7}
        >
          <Ionicons name="chevron-back" size={24} color={palette.tint} />
        </TouchableOpacity>

        {onHeaderTextPress ? (
          <TouchableOpacity
            onPress={onHeaderTextPress}
            activeOpacity={0.5}
            style={[
              styles.dateTextContainer,
              { borderColor: palette.border, backgroundColor: palette.cardBackground },
              Shadows.mdBottomRight,
            ]}
          >
            <Text
              style={[styles.dateText, { color: palette.text, fontFamily: getHeadingFont() }]}
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.7}
            >
              {headerLabel}
            </Text>
          </TouchableOpacity>
        ) : (
          <View
            style={[
              styles.dateTextContainer,
              { borderColor: palette.borderMuted, backgroundColor: palette.cardBackground },
              Shadows.smBottomRight,
            ]}
          >
            <Text
              style={[styles.dateText, { color: palette.text, fontFamily: getHeadingFont() }]}
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.7}
            >
              {headerLabel}
            </Text>
          </View>
        )}

        <TouchableOpacity
          style={styles.iconButton}
          onPress={onNextWeek}
          activeOpacity={0.7}
        >
          <Ionicons name="chevron-forward" size={24} color={palette.tint} />
        </TouchableOpacity>
      </View>

      {/* Right: Add and Today Buttons */}
      <View style={styles.rightButtons}>
        {onAddEventPress && (
          <AddEventDropdown
            onAddEvent={onAddEventPress}
            tintColor={palette.tint}
          />
        )}
        <TouchableOpacity
          style={[styles.todayButton, { borderColor: palette.tint }]}
          onPress={onTodayPress}
          activeOpacity={0.7}
        >
          <Text style={[styles.todayButtonText, { color: palette.tint }]}>
            {new Date().getDate()}
          </Text>
        </TouchableOpacity>
      </View>
    </>
  );

  if (bareContainer) {
    return (
      <View
        style={[
          styles.bareWrapper,
          { backgroundColor: palette.cardBackground, paddingTop: insets.top },
        ]}
      >
        <View style={styles.container}>{innerContent}</View>
      </View>
    );
  }

  return (
    <ScreenHeaderContainer style={styles.container}>
      {innerContent}
    </ScreenHeaderContainer>
  );
}

const styles = StyleSheet.create({
  bareWrapper: {
    zIndex: 1,
  },
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 14,
  },
  leftButtons: {
    width: 44,
    alignItems: 'flex-start',
  },
  iconButton: {
    padding: 6,
    justifyContent: 'center',
    alignItems: 'center',
  },
  navigationGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    marginHorizontal: 4,
  },
  dateTextContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1.5,
    borderRadius: BorderRadius.sm,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  dateText: {
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
    flexShrink: 1,
  },

  rightButtons: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  todayButton: {
    width: 34,
    height: 34,
    borderRadius: 8,
    borderWidth: 1.5,
    justifyContent: 'center',
    alignItems: 'center',
  },
  todayButtonText: {
    fontSize: 12,
    fontWeight: '600',
  },
});
